import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Upload, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { soundManager } from "@/lib/soundManager";
import { trpc } from "@/lib/trpc";

interface ProfilePictureUploadProps {
  currentPicture?: string | null;
  userName?: string | null;
  onUploadSuccess?: () => void;
}

export default function ProfilePictureUpload({ currentPicture, userName, onUploadSuccess }: ProfilePictureUploadProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = trpc.user.uploadProfilePicture.useMutation({
    onSuccess: () => {
      soundManager.playSuccess();
      toast.success("Profile picture updated successfully!");
      setIsOpen(false);
      setPreviewUrl(null);
      setUploading(false);
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    },
    onError: (error: any) => {
      soundManager.playError();
      toast.error(error.message || "Failed to upload profile picture");
      setUploading(false);
    },
  });

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      soundManager.playError();
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      soundManager.playError();
      toast.error("Image size must be less than 5MB");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    soundManager.playClick();

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      // Upload to server
      uploadMutation.mutate({ imageData: base64 });
    } catch (error) {
      soundManager.playError();
      toast.error("Failed to process image");
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleCameraClick = () => {
    soundManager.playClick();
    cameraInputRef.current?.click();
  };

  const handleUploadClick = () => {
    soundManager.playClick();
    fileInputRef.current?.click();
  };

  const handleProfileClick = () => {
    soundManager.playClick();
    setIsOpen(true);
  };

  return (
    <>
      {/* Profile Picture - Clickable */}
      <div onClick={handleProfileClick} className="cursor-pointer hover:opacity-80 transition-opacity relative group">
        {currentPicture ? (
          <img
            src={currentPicture}
            alt={userName || "Profile"}
            className="w-20 h-20 rounded-full border-2 border-primary/40 object-cover"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center border-2 border-primary/40">
            <User className="w-10 h-10 text-white" />
          </div>
        )}
        {/* Overlay hint */}
        <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Camera className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Profile Picture</DialogTitle>
            <DialogDescription>
              Take a photo or upload an image from your device
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Preview */}
            {previewUrl && (
              <div className="flex justify-center">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-32 h-32 rounded-full object-cover border-2 border-primary/40"
                />
              </div>
            )}

            {/* Upload Options */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="w-full h-24 flex-col gap-2"
                onClick={handleCameraClick}
                disabled={uploading}
              >
                <Camera className="w-8 h-8" />
                <span>Take Photo</span>
              </Button>
              <Button
                variant="outline"
                className="w-full h-24 flex-col gap-2"
                onClick={handleUploadClick}
                disabled={uploading}
              >
                <Upload className="w-8 h-8" />
                <span>Upload Image</span>
              </Button>
            </div>

            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileInputChange}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileInputChange}
            />

            {/* Upload Status */}
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Uploading...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
